package com.pointlessapps.granite.mica.llvm

import com.pointlessapps.granite.mica.ast.statements.AssignmentStatement
import com.pointlessapps.granite.mica.ast.statements.BreakStatement
import com.pointlessapps.granite.mica.ast.statements.ExpressionStatement
import com.pointlessapps.granite.mica.ast.statements.FunctionDeclarationStatement
import com.pointlessapps.granite.mica.ast.statements.LoopIfStatement
import com.pointlessapps.granite.mica.ast.statements.LoopStatement
import com.pointlessapps.granite.mica.ast.statements.ReturnStatement
import com.pointlessapps.granite.mica.ast.statements.Statement
import com.pointlessapps.granite.mica.ast.statements.UserInputCallStatement
import com.pointlessapps.granite.mica.ast.statements.UserOutputCallStatement
import com.pointlessapps.granite.mica.ast.statements.VariableDeclarationStatement

internal fun Statement.compile(
    context: LlvmCompilerContext,
    builder: StringBuilder,
) {
    when (this) {
        is VariableDeclarationStatement -> {
            val ptr = context.nextRegister()
            val llvmType = "i64" // Simplified to i64
            builder.append("  $ptr = alloca $llvmType\n")
            
            val rhsVal = rhs.unfoldExpression(context, builder)
            val compilerType = if (rhsVal.type == "ptr") "ptr" else "i64"
            context.declareVariable(lhsToken.value, ptr, compilerType)

            builder.append("  store ${rhsVal.type} ${rhsVal.register}, ptr $ptr\n")
        }

        is AssignmentStatement -> {
            var info = context.getVariableInfo(symbolToken.value)
            
            val rhsVal = rhs.unfoldExpression(context, builder)
            val compilerType = if (rhsVal.type == "ptr") "ptr" else "i64"
            
            var ptr = info?.ptr
            if (ptr == null && accessorExpressions.isEmpty()) {
                // Auto-declare variable if type was omitted (inference)
                ptr = context.nextRegister()
                builder.append("  $ptr = alloca i64\n")
                context.declareVariable(symbolToken.value, ptr, compilerType)
            } else if (ptr == null) {
                throw Exception("Undefined variable ${symbolToken.value}")
            } else if (accessorExpressions.isEmpty()) {
                info?.type = compilerType
            }
            
            if (accessorExpressions.isEmpty()) {
                builder.append("  store ${rhsVal.type} ${rhsVal.register}, ptr $ptr\n")
            } else {
                // ... assuming accessors are handled later (actually wait, they aren't fully handled yet in Assignment)
                builder.append("  store ${rhsVal.type} ${rhsVal.register}, ptr $ptr\n")
            }
        }

        is ExpressionStatement -> {
            expression.unfoldExpression(context, builder)
        }

        is LoopStatement -> {
            // Note: LoopStatement is sealed, but we check its instances LoopIfStatement / etc.
            // Oh wait, `LoopStatement` is sealed but `LoopIfStatement` and `LoopInStatement` inherit from it. 
            // However, `LoopStatement` itself can be instantiated if there's no if/in, wait no, it's sealed.
            // Let's handle `LoopIfStatement`
            if (this is LoopIfStatement) {
                val loopStart = context.nextBlock("loop_start")
                val loopBody = context.nextBlock("loop_body")
                val loopEnd = context.nextBlock("loop_end")
                val elseBlock = context.nextBlock("loop_else")

                context.pushLoopEndLabel(loopEnd)

                builder.append("  br label %$loopStart\n")
                builder.append("$loopStart:\n")

                if (ifConditionExpression != null) {
                    val condVal = ifConditionExpression.unfoldExpression(context, builder)
                    builder.append("  br i1 ${condVal.register}, label %$loopBody, label %$elseBlock\n")
                } else {
                    builder.append("  br label %$loopBody\n")
                }

                builder.append("$loopBody:\n")
                this.loopBody.statements.forEach { it.compile(context, builder) }
                val lastLineBody = builder.toString().trimEnd().substringAfterLast('\n').trim()
                if (!lastLineBody.startsWith("ret ") && !lastLineBody.startsWith("br ")) {
                    builder.append("  br label %$loopStart\n")
                }

                builder.append("$elseBlock:\n")
                this.elseDeclaration?.elseBody?.statements?.forEach { it.compile(context, builder) }
                val lastLineElse = builder.toString().trimEnd().substringAfterLast('\n').trim()
                if (!lastLineElse.startsWith("ret ") && !lastLineElse.startsWith("br ")) {
                    builder.append("  br label %$loopEnd\n")
                }

                builder.append("$loopEnd:\n")
                context.popLoopEndLabel()
            } else if (this is com.pointlessapps.granite.mica.ast.statements.LoopInStatement) {
                val loopStart = context.nextBlock("loop_in_start")
                val loopBody = context.nextBlock("loop_in_body")
                val loopEnd = context.nextBlock("loop_in_end")

                // Evaluate array
                val arrayVal = arrayExpression.unfoldExpression(context, builder)

                // Setup index = 0
                val idxVar = context.nextRegister()
                builder.append("  $idxVar = alloca i64\n")
                builder.append("  store i64 0, ptr $idxVar\n")

                // Get array length
                val asPtr = if (arrayVal.type == "ptr") arrayVal.register else {
                    val ptrReg = context.nextRegister()
                    builder.append("  $ptrReg = inttoptr i64 ${arrayVal.register} to ptr\n")
                    ptrReg
                }
                
                val lengthPtr = context.nextRegister()
                builder.append("  $lengthPtr = getelementptr %Array, ptr $asPtr, i32 0, i32 0\n")
                val lengthVal = context.nextRegister()
                builder.append("  $lengthVal = load i64, ptr $lengthPtr\n")

                // Get array data
                val bufferPtr = context.nextRegister()
                builder.append("  $bufferPtr = getelementptr %Array, ptr $asPtr, i32 0, i32 1\n")
                val bufferData = context.nextRegister()
                builder.append("  $bufferData = load ptr, ptr $bufferPtr\n")

                context.pushLoopEndLabel(loopEnd)
                builder.append("  br label %$loopStart\n")

                builder.append("$loopStart:\n")
                val currIdx = context.nextRegister()
                builder.append("  $currIdx = load i64, ptr $idxVar\n")
                val cond = context.nextRegister()
                builder.append("  $cond = icmp slt i64 $currIdx, $lengthVal\n")
                builder.append("  br i1 $cond, label %$loopBody, label %$loopEnd\n")

                builder.append("$loopBody:\n")
                // Fetch current item
                val elemPtr = context.nextRegister()
                builder.append("  $elemPtr = getelementptr i64, ptr $bufferData, i64 $currIdx\n")
                val elemVal = context.nextRegister()
                builder.append("  $elemVal = load i64, ptr $elemPtr\n")

                // Bind symbolToken
                val itemPtr = context.nextRegister()
                builder.append("  $itemPtr = alloca i64\n")
                builder.append("  store i64 $elemVal, ptr $itemPtr\n")
                context.pushScope()
                context.declareVariable(symbolToken.value, itemPtr)

                // Bind indexToken if exists
                if (indexToken != null) {
                    val idxStore = context.nextRegister()
                    builder.append("  $idxStore = alloca i64\n")
                    builder.append("  store i64 $currIdx, ptr $idxStore\n")
                    context.declareVariable(indexToken.value, idxStore)
                }

                this.loopBody.statements.forEach { it.compile(context, builder) }

                // Increment index
                val nextIdx = context.nextRegister()
                builder.append("  $nextIdx = add i64 $currIdx, 1\n")
                builder.append("  store i64 $nextIdx, ptr $idxVar\n")
                
                context.popScope()

                val lastLineBody = builder.toString().trimEnd().substringAfterLast('\n').trim()
                if (!lastLineBody.startsWith("ret ") && !lastLineBody.startsWith("br ")) {
                    builder.append("  br label %$loopStart\n")
                }

                builder.append("$loopEnd:\n")
                context.popLoopEndLabel()
            }
        }

        is BreakStatement -> {
            val loopEnd = context.getCurrentLoopEndLabel() ?: throw Exception("Break statement outside of a loop")
            builder.append("  br label %$loopEnd\n")
        }

        is ReturnStatement -> {
            val returnVal = returnExpression?.unfoldExpression(context, builder)
            if (returnVal != null) {
                builder.append("  ret ${returnVal.type} ${returnVal.register}\n")
            } else {
                builder.append("  ret void\n")
            }
        }

        is UserOutputCallStatement -> {
            val valToPrint = contentExpression.unfoldExpression(context, builder)
            val formatStr = when (valToPrint.type) {
                "i64" -> "@.str.int"
                "double" -> "@.str.double"
                "i1" -> "@.str.true"
                "ptr" -> "@.str.string"
                else -> "@.str.int"
            }
            if (valToPrint.type == "i1") {
                val extReg = context.nextRegister()
                builder.append("  $extReg = zext i1 ${valToPrint.register} to i64\n")
                val ignored = context.nextRegister()
                builder.append("  $ignored = call i32 (ptr, ...) @printf(ptr @.str.int, i64 $extReg)\n")
            } else {
                val ignored = context.nextRegister()
                builder.append("  $ignored = call i32 (ptr, ...) @printf(ptr $formatStr, ${valToPrint.type} ${valToPrint.register})\n")
            }
        }

        is UserInputCallStatement -> {
            var ptr = context.getVariablePtr(contentToken.value)
            if (ptr == null) {
                ptr = context.nextRegister()
                builder.append("  $ptr = alloca i64\n")
                context.declareVariable(contentToken.value, ptr)
            }
            
            // allocate a buffer for scanf
            val bufferPtr = context.nextRegister()
            builder.append("  $bufferPtr = call ptr @malloc(i64 1024)\n")
            
            val ignored = context.nextRegister()
            builder.append("  $ignored = call i32 (ptr, ...) @scanf(ptr @.str.string_fmt, ptr $bufferPtr)\n")
            
            val asInt = context.nextRegister()
            builder.append("  $asInt = ptrtoint ptr $bufferPtr to i64\n")
            builder.append("  store i64 $asInt, ptr $ptr\n")
        }

        is FunctionDeclarationStatement -> {
            val paramStr =
                parameters.joinToString(", ") { "i64 %${it.nameToken.value}" } // Assuming i64 params
            builder.append("define i64 @${nameToken.value}($paramStr) {\n")
            builder.append("entry:\n")

            // Allocate and store parameters to local variables
            parameters.forEach {
                val ptr = context.nextRegister()
                builder.append("  $ptr = alloca i64\n")
                builder.append("  store i64 %${it.nameToken.value}, ptr $ptr\n")
                context.declareVariable(it.nameToken.value, ptr)
            }

            body.forEach { it.compile(context, builder) }
            val lastLineFunc = builder.toString().trimEnd().substringAfterLast('\n').trim()
            if (!lastLineFunc.startsWith("ret ")) {
                builder.append("  ret i64 0\n") // Dummy return fallback
            }
            builder.append("}\n\n")
        }

        else -> throw Exception("Unsupported statement type in LLVM compiler: ${this::class.simpleName}")
    }
}
